import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MODULES } from "@/lib/site-data";

export default function ModulesScroll() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  // 12 cards. translate horizontally with scroll.
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-78%"]);

  return (
    <section ref={ref} id="modules" data-testid="modules-scroll-section" className="relative bg-ab-bg" style={{ height: "320vh" }}>
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="ab-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="ab-eyebrow text-ab-accent">Modules in motion</p>
              <h2 className="ab-h2 text-5xl md:text-6xl mt-4 max-w-2xl">
                Twelve modules. <span className="italic font-light">One</span> rhythm.
              </h2>
            </div>
            <p className="text-ab-muted max-w-md">
              Scroll horizontally through every module — from OPD to discharge, from pharmacy to analytics.
            </p>
          </div>
        </div>

        <motion.div style={{ x }} className="flex gap-6 pl-6 md:pl-12 will-change-transform">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <article
                key={m.id}
                data-testid={`module-card-${m.id}`}
                className="ab-card relative shrink-0 w-[78vw] sm:w-[58vw] md:w-[44vw] lg:w-[32vw] xl:w-[28vw] h-[64vh] overflow-hidden flex flex-col group"
              >
                <div className="relative h-1/2 overflow-hidden">
                  <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 text-ab-primary text-xs">
                    <Icon className="w-4 h-4" /> {m.name}
                  </div>
                </div>
                <div className="flex-1 p-6 md:p-7 flex flex-col">
                  <h3 className="font-serif text-2xl md:text-3xl text-ab-primary leading-tight">{m.title}</h3>
                  <p className="mt-2 text-ab-muted text-sm leading-relaxed">{m.summary}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-ab-text/85">
                    {m.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ab-accent shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-4 flex items-center justify-between text-sm">
                    <span className="font-mono-tech text-xs text-ab-muted">M.{String(MODULES.indexOf(m) + 1).padStart(2, "0")}</span>
                    <span className="inline-flex items-center gap-1 text-ab-primary group-hover:text-ab-accent transition">
                      Learn <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
          <div className="shrink-0 w-[10vw]" />
        </motion.div>
      </div>
    </section>
  );
}
